// PROMPT 152: "Avisos en este dispositivo": una notificacion del telefono (o de la computadora) cuando llega un
// mensaje de un cliente, aunque la plataforma este cerrada. Al tocarla se abre ese chat.
import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { nombreCorto } from "@/lib/marcasWa";
import {
  activarAvisos,
  cambiarPreferencias,
  desactivarAvisos,
  esAppInstalada,
  esIphone,
  permisoDeAvisos,
  soportaPush,
  suscripcionActual,
} from "@/lib/pushAvisos";

const AVISO_IPHONE =
  "En iPhone: abrí la plataforma en Safari → Compartir → Agregar a inicio, entrá desde ese ícono y activá los avisos ahí.";
const PREFS_INICIALES = { cuales: "todas", marcas: null };
const NOMBRE_PERMISO = {
  granted: "Permitido",
  denied: "Bloqueado",
  default: "Sin pedir todavía",
  "no-soportado": "Este navegador no lo permite",
};

function Fila({ titulo, valor, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-600">{titulo}</span>
      <span className="text-right font-medium text-slate-900" data-testid={testid}>
        {valor}
      </span>
    </div>
  );
}

function Aviso({ tono, testid, children }) {
  const colores =
    tono === "ambar"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tono === "rojo"
        ? "border-red-200 bg-red-50 text-red-800"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <p className={`rounded-md border px-3 py-2 ${colores}`} data-testid={testid}>
      {children}
    </p>
  );
}

export default function AvisosPushDialog({ open, onOpenChange, marcas }) {
  const [cargando, setCargando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [servidor, setServidor] = useState(null); // lo que contesto GET /push/estado
  const [endpoint, setEndpoint] = useState(null); // el de este navegador, si ya tiene una suscripcion
  const [permiso, setPermiso] = useState(permisoDeAvisos());
  const [prefs, setPrefs] = useState(PREFS_INICIALES);

  const soporta = soportaPush();
  const iphoneSinInstalar = esIphone() && !esAppInstalada();
  const disponible = Boolean(servidor && servidor.disponible);
  const suscripto = Boolean(endpoint && servidor && servidor.suscripto_este_endpoint);
  const lista = marcas || [];

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const sub = await suscripcionActual();
      const { data } = await api.get("/push/estado", { params: sub ? { endpoint: sub.endpoint } : {} });
      setServidor(data);
      setEndpoint(sub ? sub.endpoint : null);
      setPrefs(data.preferencias ? { ...PREFS_INICIALES, ...data.preferencias } : PREFS_INICIALES);
      setPermiso(permisoDeAvisos());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const activar = async () => {
    setTrabajando(true);
    try {
      const r = await activarAvisos(servidor.clave_publica, prefs);
      setPermiso(permisoDeAvisos());
      if (!r.ok) {
        toast.error(r.permiso === "denied" ? "El navegador bloqueó los avisos." : "No diste el permiso para los avisos.");
      } else {
        toast.success("Listo: este dispositivo va a recibir los avisos.");
        await cargar();
      }
    } catch (e) {
      toast.error(e.response ? formatApiError(e.response.data?.detail) : "No se pudieron activar los avisos en este dispositivo.");
    } finally {
      setTrabajando(false);
    }
  };

  const desactivar = async () => {
    setTrabajando(true);
    try {
      await desactivarAvisos();
      toast.success("Este dispositivo ya no recibe avisos.");
      await cargar();
    } catch {
      toast.error("No se pudieron desactivar los avisos.");
    } finally {
      setTrabajando(false);
    }
  };

  // Antes de activar solo se anota lo elegido (viaja con el alta); ya suscripto, se guarda en el momento.
  const guardarPrefs = async (nuevas) => {
    const anteriores = prefs;
    setPrefs(nuevas);
    if (!suscripto) return;
    try {
      await cambiarPreferencias(endpoint, nuevas);
    } catch (e) {
      setPrefs(anteriores);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const alternarMarca = (slug) => {
    const actuales = prefs.marcas || [];
    const nuevas = actuales.includes(slug) ? actuales.filter((s) => s !== slug) : [...actuales, slug];
    guardarPrefs({ ...prefs, marcas: nuevas.length ? nuevas : null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="wa-push-dialog">
        <DialogHeader>
          <DialogTitle>Avisos en este dispositivo</DialogTitle>
          <DialogDescription>
            Una notificación del teléfono (o de la computadora) cuando llega un mensaje de un cliente, aunque la plataforma
            esté cerrada. Tocás el aviso y se abre ese chat.
          </DialogDescription>
        </DialogHeader>
        {cargando && !servidor ? (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="wa-push-cargando">
            <Loader2 className="h-4 w-4 animate-spin" /> Mirando el estado…
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {error ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800" role="alert" data-testid="wa-push-error">
                {error}
              </p>
            ) : null}
            <section className="space-y-1.5" data-testid="wa-push-estado">
              <Fila titulo="Avisos del servidor" valor={disponible ? "Disponibles" : "Sin configurar"} testid="wa-push-servidor" />
              <Fila titulo="Permiso del navegador" valor={NOMBRE_PERMISO[permiso] || permiso} testid="wa-push-permiso" />
              <Fila titulo="Este dispositivo" valor={suscripto ? "Recibe avisos" : "No recibe avisos"} testid="wa-push-suscripto" />
            </section>
            {servidor && !disponible ? (
              <Aviso testid="wa-push-no-configurado">
                Los avisos al teléfono todavía no están activados en el servidor: los prende el coordinador.
              </Aviso>
            ) : null}
            {iphoneSinInstalar ? (
              <Aviso tono="ambar" testid="wa-push-iphone">
                {AVISO_IPHONE}
              </Aviso>
            ) : null}
            {!soporta && !iphoneSinInstalar ? (
              <Aviso testid="wa-push-no-soportado">
                Este navegador no permite avisos. Probá con Chrome o, en iPhone, con la plataforma agregada a la pantalla de inicio.
              </Aviso>
            ) : null}
            {permiso === "denied" ? (
              <Aviso tono="ambar" testid="wa-push-bloqueado">
                Los avisos están bloqueados en este navegador. Para habilitarlos: tocá el candado (o el ícono de ajustes) a la
                izquierda de la dirección, entrá en “Notificaciones”, elegí “Permitir” y recargá la página.
              </Aviso>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {suscripto ? (
                <Button variant="outline" size="sm" onClick={desactivar} disabled={trabajando} data-testid="wa-push-desactivar">
                  {trabajando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                  Desactivar
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={activar}
                  disabled={trabajando || !disponible || !soporta || iphoneSinInstalar || permiso === "denied"}
                  data-testid="wa-push-activar"
                >
                  {trabajando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                  Activar avisos
                </Button>
              )}
            </div>
            {disponible && soporta && !iphoneSinInstalar ? (
              <section className="space-y-2" data-testid="wa-push-prefs">
                <label htmlFor="wa-push-cuales" className="block font-medium text-slate-900">
                  Avisarme de
                </label>
                <select
                  id="wa-push-cuales"
                  value={prefs.cuales}
                  onChange={(e) => guardarPrefs({ ...prefs, cuales: e.target.value })}
                  className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 sm:text-sm"
                  data-testid="wa-push-cuales"
                >
                  <option value="todas">Todas las conversaciones</option>
                  <option value="mias_y_sin_asignar">Las mías y las sin asignar</option>
                </select>
                {lista.length > 0 ? (
                  <fieldset className="space-y-1">
                    <legend className="font-medium text-slate-900">Marcas</legend>
                    <p className="text-xs text-slate-500">Si no tildás ninguna, te aviso de todas.</p>
                    {lista.map((m) => (
                      <label key={m.slug} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-emerald-600"
                          checked={(prefs.marcas || []).includes(m.slug)}
                          onChange={() => alternarMarca(m.slug)}
                          data-testid={`wa-push-marca-${m.slug}`}
                        />
                        <span>{nombreCorto(m.slug, lista)}</span>
                      </label>
                    ))}
                  </fieldset>
                ) : null}
                <p className="text-xs text-slate-500">
                  Si tenés la plataforma abierta y a la vista, el aviso del teléfono no suena: llega recién cuando pasan más de
                  2 minutos sin usarla.
                </p>
              </section>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
